/* eslint-disable react-refresh/only-export-components */
import * as React from "react"
import { useShallowCompareEffect } from "react-use"
import * as SelectPrimitive from "@radix-ui/react-select"
import { cva, type VariantProps } from "class-variance-authority"
import { Check, ChevronDown, ChevronUp } from "lucide-react"

import { cn } from "@/helpers/classNames"

export const selectTriggerVariants = cva(
  `
    flex h-10 w-full items-center justify-between rounded-md border border-input
    bg-background px-3 py-2 ring-offset-background
    placeholder:text-muted-foreground
    focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:outline-hidden
    disabled:cursor-not-allowed disabled:bg-neutral-200
    disabled:text-neutral-400
    [&>span]:line-clamp-1
  `,
  {
    variants: {
      size: {
        xs: "h-8 text-xs",
        default: "text-sm",
        lg: "font-button-small",
        xl: "font-button-large",
      },
    },
  },
)

type SelectVariant = VariantProps<typeof selectTriggerVariants>

const Select = SelectPrimitive.Root

function SelectGroup({
  ref,
  unmask = false,
  ...restProps
}: {
  ref?: React.Ref<React.ElementRef<typeof SelectPrimitive.Group>>
} & {
  unmask?: boolean
} & React.ComponentPropsWithoutRef<typeof SelectPrimitive.Group>) {
  return (
    <SelectPrimitive.Group
      {...restProps}
      ref={ref}
      {...(unmask && { "data-unmask": true })}
    />
  )
}

function SelectValue({
  ref,
  unmask = false,
  ...restProps
}: {
  ref?: React.Ref<React.ElementRef<typeof SelectPrimitive.Value>>
} & {
  unmask?: boolean
} & React.ComponentPropsWithoutRef<typeof SelectPrimitive.Value>) {
  return (
    <SelectPrimitive.Value
      {...restProps}
      ref={ref}
      {...(unmask && { "data-unmask": true })}
    />
  )
}

function SelectTrigger({
  children,
  className,
  ref,
  size,
  ...props
}: {
  ref?: React.Ref<React.ElementRef<typeof SelectPrimitive.Trigger>>
} & React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger> &
  SelectVariant) {
  return (
    <SelectPrimitive.Trigger
      ref={ref}
      className={cn(selectTriggerVariants({ size }), className)}
      {...props}
    >
      {children}
      <SelectPrimitive.Icon asChild>
        <ChevronDown className="size-4 opacity-50" />
      </SelectPrimitive.Icon>
    </SelectPrimitive.Trigger>
  )
}

function SelectScrollUpButton({
  className,
  ref,
  ...props
}: {
  ref?: React.Ref<React.ElementRef<typeof SelectPrimitive.ScrollUpButton>>
} & React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollUpButton>) {
  return (
    <SelectPrimitive.ScrollUpButton
      ref={ref}
      className={cn(
        "flex cursor-default items-center justify-center py-1",
        className,
      )}
      {...props}
    >
      <ChevronUp className="size-4" />
    </SelectPrimitive.ScrollUpButton>
  )
}

function SelectScrollDownButton({
  className,
  ref,
  ...props
}: {
  ref?: React.Ref<React.ElementRef<typeof SelectPrimitive.ScrollDownButton>>
} & React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollDownButton>) {
  return (
    <SelectPrimitive.ScrollDownButton
      ref={ref}
      className={cn(
        "flex cursor-default items-center justify-center py-1",
        className,
      )}
      {...props}
    >
      <ChevronDown className="size-4" />
    </SelectPrimitive.ScrollDownButton>
  )
}

function SelectContent({
  children,
  className,
  position = "popper",
  ref,
  ...props
}: {
  ref?: React.Ref<React.ElementRef<typeof SelectPrimitive.Content>>
} & React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content>) {
  return (
    <SelectPrimitive.Portal>
      <SelectPrimitive.Content
        ref={ref}
        className={cn(
          `
            relative z-50 max-h-96 min-w-32 overflow-hidden rounded-md border
            bg-popover text-popover-foreground shadow-md
            data-[side=bottom]:slide-in-from-top-2
            data-[side=left]:slide-in-from-right-2
            data-[side=right]:slide-in-from-left-2
            data-[side=top]:slide-in-from-bottom-2
            data-[state=closed]:animate-out data-[state=closed]:fade-out-0
            data-[state=closed]:zoom-out-95
            data-[state=open]:animate-in data-[state=open]:fade-in-0
            data-[state=open]:zoom-in-95
          `,
          position === "popper" &&
            `
              data-[side=bottom]:translate-y-1
              data-[side=left]:-translate-x-1
              data-[side=right]:translate-x-1
              data-[side=top]:-translate-y-1
            `,
          className,
        )}
        position={position}
        {...props}
      >
        <SelectScrollUpButton />
        <SelectPrimitive.Viewport
          className={cn(
            "p-1",
            position === "popper" &&
              `
                h-(--radix-select-trigger-height) w-full
                min-w-(--radix-select-trigger-width)
              `,
          )}
        >
          {children}
        </SelectPrimitive.Viewport>
        <SelectScrollDownButton />
      </SelectPrimitive.Content>
    </SelectPrimitive.Portal>
  )
}

function SelectLabel({
  className,
  ref,
  ...props
}: {
  ref?: React.Ref<React.ElementRef<typeof SelectPrimitive.Label>>
} & React.ComponentPropsWithoutRef<typeof SelectPrimitive.Label>) {
  return (
    <SelectPrimitive.Label
      ref={ref}
      className={cn(
        "px-2 py-1.5 text-xs text-muted-foreground uppercase",
        className,
      )}
      {...props}
    />
  )
}

export const selectItemVariants = cva(
  `
    relative flex w-full cursor-default items-center rounded-sm py-1.5 pr-2 pl-8
    outline-hidden select-none
    focus:bg-accent focus:text-accent-foreground
    data-disabled:pointer-events-none data-disabled:bg-neutral-400
    data-disabled:opacity-50
  `,
  {
    variants: {
      size: {
        xs: "text-xs",
        default: "text-sm",
        lg: "text-base font-bold",
        xl: "font-button-medium",
      },
    },
  },
)

function SelectItem({
  children,
  className,
  ref,
  size,
  unmask,
  ...props
}: {
  ref?: React.Ref<React.ElementRef<typeof SelectPrimitive.Item>>
} & { unmask?: boolean } & React.ComponentPropsWithoutRef<
    typeof SelectPrimitive.Item
  > &
  SelectVariant) {
  return (
    <SelectPrimitive.Item
      ref={ref}
      className={cn(selectItemVariants({ size }), className)}
      {...props}
    >
      {/* eslint-disable-next-line better-tailwindcss/enforce-consistent-line-wrapping */}
      <span className="absolute left-2 flex size-3.5 items-center justify-center">
        <SelectPrimitive.ItemIndicator>
          <Check className="size-4" />
        </SelectPrimitive.ItemIndicator>
      </span>

      <SelectPrimitive.ItemText {...(unmask && { "data-unmask": true })}>
        {children}
      </SelectPrimitive.ItemText>
    </SelectPrimitive.Item>
  )
}

type Props = {
  className?: string
  disabled?: boolean
  onChange: (value: string) => void
  options: { label: string; value: string }[]
  placeholder: string
  unmask?: boolean
  value: string
} & SelectVariant

export function SelectWithOptions({
  className,
  disabled,
  onChange,
  options,
  placeholder,
  ref,
  size,
  unmask = false,
  value,
  ...rest
}: {
  ref?: React.Ref<React.ElementRef<typeof SelectTrigger>>
} & Props) {
  useShallowCompareEffect(() => {
    if (options.length === 1 && value !== options[0].value) {
      onChange(options[0].value)
    }
  }, [options])

  return (
    <Select
      value={value}
      onValueChange={onChange}
      disabled={disabled || !options.length}
    >
      <SelectTrigger className={className} size={size} ref={ref} {...rest}>
        <SelectValue placeholder={placeholder} unmask={unmask} />
      </SelectTrigger>
      <SelectContent>
        <SelectGroup className="flex flex-col gap-2 p-3">
          {options.map((option) => (
            <SelectItem
              key={option.value}
              value={option.value}
              size={size}
              unmask={unmask}
            >
              {option.label}
            </SelectItem>
          ))}
        </SelectGroup>
      </SelectContent>
    </Select>
  )
}

export {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
}
